import styled from "@emotion/styled";
import { ButtonBase, Paper } from "@mui/material";

import {
  collapseTransition,
  SIDEBAR_RAIL_TILE
} from "../MainSidebar.components";

const EASE = "cubic-bezier(0.2, 0, 0.2, 1)";

export const SCOPE_PANEL_WIDTH = 300;

/**
 * The initials tile. The scope card wears a 28px one, the rail a 34px one,
 * and the panel's rows a 24px one — same tile, three sizes, so the initials
 * read as one identity wherever they appear.
 */
const ScopeTile = styled("span", {
  shouldForwardProp: (prop: PropertyKey) =>
    prop !== "size" && prop !== "tone" && prop !== "open"
})<{ size: number; tone: "tint" | "card" | "hover"; open?: boolean }>(({
  open,
  size,
  theme,
  tone
}) => {
  const { card, item } = theme.vars.ui.sidebar;
  const ring =
    tone === "tint"
      ? item.selected.ring
      : "rgba(var(--mui-palette-text-primaryChannel) / 0.13)";

  return {
    alignItems: "center",
    backgroundColor:
      tone === "tint"
        ? item.selected.background
        : tone === "card"
          ? card.background
          : card.hover,
    borderRadius: size >= 34 ? 10 : size >= 28 ? 8 : 7,
    boxShadow: `inset 0 0 0 1px ${ring}`,
    boxSizing: "border-box",
    color: tone === "tint" ? item.selected.text : "inherit",
    cursor: "inherit",
    display: "inline-flex",
    flex: "none",
    fontSize: size >= 28 ? theme.typography.caption.fontSize : 9.5,
    fontWeight: theme.typography.fontWeightSemiBold,
    height: size,
    justifyContent: "center",
    letterSpacing: "0.02em",
    lineHeight: 1,
    // The open state: a 2px glow outline standing off the tile.
    outline: open ? "2px solid var(--mui-palette-primary-glow)" : "none",
    outlineOffset: 2,
    // The tile grows into the rail's larger box as the nav folds.
    transition: collapseTransition(theme, ["width", "height"]),
    width: size
  };
});

/**
 * The trigger, in both nav states. Expanded it is a bordered card holding the
 * tile, the name and the sub-line; collapsed it folds down to the tile alone.
 *
 * One control rather than two swapped ones: the card and the rail tile used to
 * be separate elements chosen on `expanded`, so mid-fold one vanished and the
 * other appeared — the jump you could see when closing the nav.
 */
const ScopeCard = styled(ButtonBase, {
  shouldForwardProp: (prop: PropertyKey) =>
    prop !== "open" && prop !== "expanded"
})<{ open: boolean; expanded: boolean }>(({ expanded, open, theme }) => {
  const { card, line } = theme.vars.ui.sidebar;
  return {
    "&:hover": {
      borderColor:
        !expanded || open
          ? "transparent"
          : "rgba(var(--mui-palette-text-primaryChannel) / 0.35)"
    },
    alignItems: "center",
    backgroundColor: expanded ? card.background : "transparent",
    border: `1px solid ${expanded ? line : "transparent"}`,
    borderRadius: 10,
    boxSizing: "border-box",
    color: theme.vars.palette.text.primary,
    cursor: "pointer",
    display: "flex",
    flex: "none",
    gap: expanded ? 10 : 0,
    height: expanded ? "auto" : SIDEBAR_RAIL_TILE,
    justifyContent: expanded ? "flex-start" : "center",
    outline: open ? "2px solid var(--mui-palette-primary-glow)" : "none",
    outlineOffset: 2,
    // Tight on the left by arithmetic, not by eye: 12 (nav gutter) + 1
    // (border) + 4 + half of a 26px tile is 30, where the rail's tile centre
    // and every menu icon also sit.
    padding: expanded ? "6px 8px 6px 4px" : 0,
    textAlign: "left",
    transition: [
      collapseTransition(theme, [
        "background-color",
        "gap",
        "padding",
        "width"
      ]),
      `border-color 120ms ${EASE}`
    ].join(", "),
    width: expanded ? "100%" : SIDEBAR_RAIL_TILE
  };
});

const ScopeName = styled("span", {
  shouldForwardProp: (prop: PropertyKey) => prop !== "compact"
})<{ compact?: boolean }>(({ compact, theme }) => ({
  fontSize: compact ? theme.typography.body2.fontSize : 14,
  fontWeight: theme.typography.fontWeightSemiBold,
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

const ScopeSub = styled("span")(({ theme }) => ({
  color: "rgba(var(--mui-palette-text-primaryChannel) / 0.6)",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.25,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

/** The floating list of scopes. */
const ScopePanel = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.vars.ui.sidebar.card.background,
  backgroundImage: "none",
  border: `1px solid ${theme.vars.ui.sidebar.line}`,
  borderRadius: 16,
  boxShadow: "0 30px 80px rgba(3, 26, 22, 0.35)",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  maxHeight: "calc(100vh - 140px)",
  overflowY: "auto",
  padding: 9,
  width: SCOPE_PANEL_WIDTH
}));

const ScopeCaption = styled("div")(({ theme }) => ({
  color: "rgba(var(--mui-palette-text-primaryChannel) / 0.55)",
  fontSize: theme.typography.menuCaption.fontSize,
  fontWeight: theme.typography.fontWeightSemiBold,
  letterSpacing: "0.8px",
  lineHeight: 1.2,
  padding: "6px 10px 4px",
  textTransform: "uppercase"
}));

/** One scope in the panel. */
const ScopeRow = styled(ButtonBase, {
  shouldForwardProp: (prop: PropertyKey) => prop !== "selected"
})<{ selected: boolean }>(({ selected, theme }) => {
  const { card, item } = theme.vars.ui.sidebar;
  return {
    "&:hover": {
      backgroundColor: selected ? item.selected.background : card.hover
    },
    alignItems: "center",
    backgroundColor: selected ? item.selected.background : "transparent",
    borderRadius: 9,
    boxShadow: selected ? `inset 0 0 0 1px ${item.selected.ring}` : "none",
    boxSizing: "border-box",
    color: theme.vars.palette.text.primary,
    cursor: selected ? "default" : "pointer",
    display: "flex",
    flex: "none",
    gap: 10,
    height: 44,
    justifyContent: "flex-start",
    padding: "0 9px",
    textAlign: "left",
    transition: theme.transitions.create("background-color", {
      duration: theme.transitions.duration.shortest
    }),
    width: "100%"
  };
});

const ScopeDivider = styled("div")(({ theme }) => ({
  backgroundColor: theme.vars.ui.sidebar.line,
  flex: "none",
  height: 1,
  margin: "6px 2px",
  opacity: 0.6
}));

const ScopeFooterLink = styled(ButtonBase)(({ theme }) => ({
  alignItems: "center",
  borderRadius: 6,
  color: theme.vars.palette.text.primary,
  display: "inline-flex",
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightSemiBold,
  gap: 6,
  justifyContent: "flex-start",
  margin: "2px 4px 2px 10px",
  padding: "4px 0"
}));

export {
  ScopeCaption,
  ScopeCard,
  ScopeDivider,
  ScopeFooterLink,
  ScopeName,
  ScopePanel,
  ScopeRow,
  ScopeSub,
  ScopeTile
};
